import useEnrollableDevices from '@/app/hooks/useEnrollableDevices'
import { ID } from '@/types/model'
import { Button, LoadingAnimation, Text } from '@/components'
import {
  DeviceIdSelections,
  EnrollableDeviceChecklist,
} from '@/components/EnrollableDeviceChecklist/EnrollableDeviceChecklist'
import { useState } from 'react'
import { getDeviceSelectionCount } from '@/components/ProgramCard/AdditionalEnrollmentModal'
import { AlertMessage } from 'app-components'
import useEnrollmentActions from '@/hooks/useEnrollmentActions'
import { logEvent } from '@/logging'
import { match } from 'ts-pattern'

export default function EnrollAdditionalField(props: {
  programId: ID
  onSuccess: () => void
}) {
  const {
    enrollableDeviceCount,
    enrollableVehicles,
    enrollableChargers,
    enrollableThermostats,
  } = useEnrollableDevices(props.programId)
  const [deviceSelections, setDeviceSelections] = useState<DeviceIdSelections>()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { enrollDevices, logRequestProgramEnrollment } = useEnrollmentActions()

  const selectedDeviceCount = getDeviceSelectionCount(deviceSelections)

  const onSubmit = async () => {
    if (!deviceSelections) {
      return
    }

    setIsSubmitting(true)
    setError(null)
    logRequestProgramEnrollment({
      vehicles: enrollableVehicles,
      chargers: enrollableChargers,
      thermostats: enrollableThermostats,
      selections: deviceSelections,
      utilityProgramId: props.programId,
    })

    const response = await enrollDevices({
      utilityProgramId: props.programId,
      vehicleIds: deviceSelections?.vehicleIds ?? [],
      thermostatIds: deviceSelections?.thermostatIds ?? [],
      chargerIds: deviceSelections?.chargerIds ?? [],
      skipInvalidation: true,
    })

    if (response.isErr()) {
      logEvent(`Error enrolling profile`)
      setError(
        'There was an error enrolling these devices. Please try again, or make a different selection.',
      )
      setIsSubmitting(false)
      return
    }

    // Success!
    setIsSubmitting(false)
    props.onSuccess()
  }

  const ctaText =
    selectedDeviceCount === 0
      ? 'Select A Device To Enroll'
      : selectedDeviceCount > 1
      ? `Enroll Additional Devices (${selectedDeviceCount})`
      : `Enroll Additional Device (${selectedDeviceCount})`

  return (
    <section className="flex flex-col gap-4 border border-solid border-themed-grey-100 w-full rounded-md p-4">
      {error && <AlertMessage variant="error">{error}</AlertMessage>}
      <Text variant="h3">
        Enroll Additional Devices ({enrollableDeviceCount})
      </Text>
      {match(isSubmitting)
        .with(true, () => {
          return (
            <>
              <LoadingAnimation type="falling" centered />
              <Text variant="body2">
                Enrolling your devices, please wait...
              </Text>
            </>
          )
        })
        .with(false, () => {
          return (
            <EnrollableDeviceChecklist
              programId={props.programId}
              onValuesChanged={setDeviceSelections}
            />
          )
        })
        .exhaustive()}
      <Button
        id="enroll-additional-button"
        small
        variant="primary"
        disabled={selectedDeviceCount === 0 || isSubmitting}
        onClick={onSubmit}
      >
        {ctaText}
      </Button>
    </section>
  )
}
